import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Container, Menu, Skeleton, Stack, Text, Title, UnstyledButton } from '@mantine/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { biteScopeFrom } from '../lib/play-all';
import type { Gloss, Line, MemoryStateSummary, Story } from '@nelnela/contracts';
import { useStudy } from '../app/study';
import { api, ApiError } from '../lib/api';
import { instantLookup, settle } from '../lib/instant';
import { itemIdsOf, marksFor, type Marks } from '../lib/marks';
import { readBadge } from '../lib/library';
import {
  generatingLine,
  isGeorgianQuestion,
  planCounts,
  storyLines,
  storyTitle,
} from '../lib/reader';
import { decideWordTap, savedKey, savedKeys, type ArmedTap } from '../lib/save-gesture';
import type { Token } from '../lib/text';
import { useExposure } from '../lib/use-exposure';
import { useLiveProfile } from '../lib/use-profile';
import { startActivity, type ActivityHandle, type EmitInput } from '../lib/session';
import { speak, ttsCalmLine, useTtsKeyStatus } from '../lib/tts-player';
import { ulid } from '../lib/ulid';
import {
  answered,
  answerLatencyMs,
  expectedText,
  currentExercise,
  initRun,
  isDone,
  next as advance,
  skip as skipPrompt,
  type RunState,
} from '../lib/review-run';
import { StoryReader } from '../components/reader/StoryReader';
import { LibraryShelf } from '../components/reader/LibraryShelf';
import { CultureNote } from '../components/reader/CultureNote';
import { PiecePlayer } from '../components/reader/PiecePlayer';
import {
  GlossDrawer,
  type GlossSheetState,
  type HearingState,
  type SavedState,
} from '../components/watch/GlossSheet';
import { EnGlyph, IconBack, IconMore } from '../components/watch/icons';
import { TranscriptRow } from '../components/watch/Transcript';
import type { EnMode } from '../components/watch/Transcript';

/**
 * F10 Read — the story lane (design `5c` start, `5a` reader, `5b` end).
 *
 * Two screens and one rule: the reader is the transcript with the timeline taken out. Everything
 * a learner can do to a line in Watch — tap a word, open a chunk, reveal the EN, wear the marks —
 * they do here in the same component, because a second reading surface would be a second place
 * for every reading bug to live. What is genuinely new is at the two ends: a start state that has
 * to make a 10–20 s synchronous LLM call bearable, and an end state that spends the two
 * comprehension checks the generator wrote alongside the story.
 */

/** the scheduler's plan is at most this many due items (`M6a` step 2) — the honest cap to promise */
const PLAN_TARGET_CAP = 8;

/** the marks' "nothing known yet", shared so its identity never changes between renders */
const NO_MEMORY: Readonly<Record<string, MemoryStateSummary>> = Object.freeze({});

/**
 * What a gloss lookup on this screen is ABOUT (M6e §6).
 *
 * Every tap used to be on a story sentence, so `lookup` took a `Line` and read three fields off
 * it. Then the end checks' question became Georgian and tappable — and a question is not a
 * sentence of the story: it has no index into `story_item` and no translation to have been
 * visible. Naming the three fields makes that difference explicit at every call site instead of
 * hiding it behind a `Line` with two of its properties quietly wrong.
 */
interface LookupSource {
  /** the sheet's `lineId`, and the memo key's other half */
  id: string;
  /** the sentence the selection sits in — a story sentence, or the question itself */
  text: string;
  /** the story sentence's index, or null when the text is not a sentence of the story */
  sentenceIdx: number | null;
  /** was the EN visible when the tap happened? Meaningless for a question, hence optional */
  translationVisible?: boolean;
}

/** A check question has no "••• reveal" affordance — `gutter: 'none'` never draws one, and the
 *  prop is required. Shared so the memoised row's identity does not change every render. */
const NOOP_REVEAL = (): void => undefined;

const sourceOf = (line: Line): LookupSource => ({
  id: line.id,
  text: line.text,
  sentenceIdx: line.idx,
  translationVisible: !!line.translation,
});

/**
 * The reader's exposure gate keeps the once-per-sentence rule and drops the document-visibility
 * one, which is Watch's and not this screen's.
 *
 * Watch emits off the playhead, which runs whether or not anyone is looking, so it has to ask the
 * document. A reader's row exists only because `createDwellLedger` measured a sentence sitting on
 * screen — the attention is what produced the row, not a condition to re-check when it is sent.
 * And the moment these rows are settled is precisely the moment the document goes hidden: the
 * ledger flushes on `visibilitychange`/`pagehide` so background time is never counted as reading,
 * and `document.visibilityState` is already 'hidden' inside those handlers. Asking again there
 * would silently discard the last sentences of every read that ends by switching app — which is
 * how most reads end. (The queue persists what it cannot send, so a row enqueued on the way out
 * still lands on the next load.)
 */
const ALREADY_ATTENDED = { visible: () => true };

// ---------------------------------------------------------------------------------------------
// /read — start and generating (`5c`)
// ---------------------------------------------------------------------------------------------

export function Read() {
  const study = useStudy();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const profileId = study.me.profiles[0]?.id ?? null;
  const profile = study.me.profiles[0] ?? null;

  const [topic, setTopic] = useState('');
  const [pasting, setPasting] = useState(false);
  const [pasted, setPasted] = useState('');
  const [busy, setBusy] = useState<'generate' | 'import' | null>(null);
  const [calm, setCalm] = useState<string | null>(null);
  // How far short the paste is, or null when there is nothing to say. Shown BESIDE the "text
  // only" note rather than instead of it: a short link would otherwise be met with advice to
  // make it longer, which is the opposite of what that note exists to say.
  const shortBy = IMPORT_MIN - pasted.trim().length;
  const short = pasted.trim().length > 0 && shortBy > 0 ? shortBy : null;

  const recent = useQuery({
    queryKey: ['stories'],
    queryFn: () => api.storyList({ limit: 8 }),
  });
  // The generating line promises numbers, and these are the only ones we can honestly put in it
  // before the story exists: the plan is drawn from the due list, capped at eight. How many of
  // those are NEW words is the scheduler's decision and is not knowable here, so that clause is
  // simply not written rather than guessed at.
  const due = useQuery({
    queryKey: ['review-summary', profileId],
    queryFn: () => api.reviewSummary(profileId!),
    enabled: !!profileId,
    staleTime: 60_000,
  });

  const start = useCallback(
    async (run: () => Promise<Story>, kind: 'generate' | 'import') => {
      setCalm(null);
      setBusy(kind);
      try {
        const story = await run();
        void queryClient.invalidateQueries({ queryKey: ['stories'] });
        navigate(`/read/${story.id}`);
      } catch (err) {
        setBusy(null);
        setCalm(startErrorLine(err, kind));
      }
    },
    [navigate, queryClient],
  );

  return (
    <Container size={640} py="lg">
      <Stack gap="md">
        <Title order={2}>Read</Title>

        {busy === 'generate' ? (
          <GeneratingCard
            working={Math.min(PLAN_TARGET_CAP, due.data?.dueCount ?? 0)}
            medianMs={due.data?.storyGenMs ?? null}
          />
        ) : (
          <div style={panel}>
            <div style={{ fontSize: 14, color: 'var(--nn-text-strong)' }}>Read a story</div>
            <input
              className="nn-reader-input"
              value={topic}
              disabled={busy !== null}
              onChange={(e) => setTopic(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void start(() => generate(topic), 'generate');
              }}
              placeholder="topic (optional) — e.g. at the market"
              aria-label="Topic (optional)"
              maxLength={TOPIC_MAX}
              style={{ marginTop: 9 }}
            />
            <div style={{ marginTop: 11, display: 'flex', gap: 10, alignItems: 'center' }}>
              <UnstyledButton
                disabled={busy !== null}
                onClick={() => void start(() => generate(topic), 'generate')}
                style={emberButton}
              >
                Generate
              </UnstyledButton>
              <UnstyledButton
                onClick={() => setPasting((p) => !p)}
                style={{ fontSize: 12, color: 'var(--nn-link)' }}
              >
                {pasting ? 'Never mind' : 'Paste text'}
              </UnstyledButton>
            </div>
            {pasting && (
              <div style={{ marginTop: 11 }}>
                <textarea
                  className="nn-reader-input"
                  value={pasted}
                  onChange={(e) => setPasted(e.target.value)}
                  rows={5}
                  lang="ka"
                  aria-label="Text to read"
                  maxLength={IMPORT_MAX}
                  placeholder="Paste Georgian text — it stays yours and private."
                  style={{ resize: 'vertical' }}
                />
                <div style={{ marginTop: 9, display: 'flex', gap: 10, alignItems: 'center' }}>
                  <UnstyledButton
                    disabled={busy !== null || pasted.trim().length < IMPORT_MIN}
                    onClick={() => void start(() => imported(pasted), 'import')}
                    style={emberButton}
                  >
                    {busy === 'import' ? 'Reading it…' : 'Use this text'}
                  </UnstyledButton>
                  {/* A URL would mean the server fetching an arbitrary address on the learner's
                      behalf, which M6a declined to build. Say so where the paste box is. */}
                  <span style={{ fontSize: 11, color: 'var(--nn-faint)' }}>
                    Text only — a link can’t be fetched for you.
                    {short !== null && (
                      <>
                        {' '}
                        <span style={{ color: 'var(--nn-muted)' }}>
                          {short} more character{short === 1 ? '' : 's'} to go.
                        </span>
                      </>
                    )}
                  </span>
                </div>
              </div>
            )}
            {calm && (
              <Text fz={11.5} c="var(--nn-muted)" mt={10} role="status">
                {calm}
              </Text>
            )}
          </div>
        )}

        <RecentStories
          page={recent.data?.items ?? null}
          loading={recent.isPending}
          failed={recent.isError}
        />

        {profile && (
          <LibraryShelf
            targetLang={profile.targetLang}
            baseLang={profile.baseLang}
            levelEstimate={study.me.profiles[0]?.levelEstimate ?? null}
          />
        )}
      </Stack>
    </Container>
  );
}

const generate = (topic: string): Promise<Story> =>
  api.storyGenerate({
    targetLang: 'ka',
    baseLang: 'en',
    ...(topic.trim() ? { topic: topic.trim() } : {}),
  });

/**
 * The bounds `StoryImportSchema` and `StoryGenerateSchema` enforce, mirrored at the input.
 *
 * The form gated "Use this text" on length > 0 against a server minimum of 20 and capped nothing,
 * so a short paste 422'd — and a 422 puts its reason in `errors[]`, leaving `detail` undefined, so
 * the learner saw "try again in a moment" on a failure no retry could clear.
 */
const IMPORT_MIN = 20;
const IMPORT_MAX = 20_000;
const TOPIC_MAX = 120;

const imported = (text: string): Promise<Story> =>
  api.storyImport({ targetLang: 'ka', baseLang: 'en', text: text.trim() });

function startErrorLine(err: unknown, kind: 'generate' | 'import'): string {
  if (err instanceof ApiError) {
    const { status, detail } = err.problem;
    if (status === 403) return detail || 'Stories are part of the paid layer.';
    if (status === 429) return detail || 'The AI helper is resting for a bit — try again shortly.';
    if (status === 502)
      return 'That one didn’t come out readable. Try again — a second attempt usually lands.';
    if (detail) return detail;
    // A 422 leaves `detail` undefined and puts the reason in `errors[]` (validate/index.ts).
    // Without this a rejected paste showed "try again in a moment" — a permanent failure dressed
    // as a transient one, and no retry could ever clear it.
    const field = err.problem.errors?.[0]?.message;
    if (status === 422 && field) return field;
  }
  return kind === 'import'
    ? 'That text didn’t go through. Try again in a moment.'
    : 'The story didn’t arrive. Try again in a moment.';
}

/**
 * `5c`'s generating state: three shimmer lines, never a spinner.
 *
 * Generation is one Sonnet call in the request. A spinner would say only "wait"; three lines of a
 * story taking shape say what is being waited for, and the item-plan line says why it is worth
 * waiting for. This is the whole design of the wait.
 *
 * M6e §7 replaced a hard-coded "~10 s" with a MEASURED one — the median of the last 30 days of
 * successful `story_gen` calls, which rides the `reviewSummary` this screen already reads — and
 * with a count of the seconds actually spent. The counter is the load-bearing half: a static
 * estimate that has passed says nothing about whether anything is still happening.
 *
 * `aria-live="polite"` is on the panel and NOT on the counting line, deliberately: a screen
 * reader announcing a new number every second is unusable. The line the reader hears is "Writing
 * at your edge…", once.
 */
function GeneratingCard({ working, medianMs }: { working: number; medianMs: number | null }) {
  const [startedAt] = useState(() => Date.now());
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setElapsed(Date.now() - startedAt), 1000);
    return () => clearInterval(t);
  }, [startedAt]);

  return (
    <div style={panel} role="status" aria-live="polite">
      <div style={{ fontSize: 13, color: 'var(--nn-text)' }}>Writing at your edge…</div>
      <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 7 }}>
        {['100%', '82%', '64%'].map((w, i) => (
          <span
            key={w}
            className="nn-shimmer"
            style={{ width: w, animationDelay: `${i * 0.15}s` }}
            aria-hidden
          />
        ))}
      </div>
      <div
        style={{ marginTop: 10, fontSize: 11.5, color: 'var(--nn-muted)' }}
        aria-live="off"
        lang="en"
      >
        {generatingLine(working, elapsed, medianMs)}
      </div>
    </div>
  );
}

function RecentStories({
  page,
  loading,
  failed,
}: {
  page: Story[] | null;
  loading: boolean;
  failed: boolean;
}) {
  if (loading) {
    return (
      <Stack gap={8}>
        <Skeleton height={13} width="30%" radius="sm" />
        <Skeleton height={44} radius="sm" />
        <Skeleton height={44} radius="sm" />
      </Stack>
    );
  }
  if (failed || !page || page.length === 0) {
    return (
      <Text fz={12} c="var(--nn-faint)">
        {failed
          ? 'Your stories will be here when the connection is.'
          : 'Stories you read collect here.'}
      </Text>
    );
  }
  return (
    <Stack gap={8}>
      <Text fz={11} c="var(--nn-faint)" tt="uppercase" style={{ letterSpacing: '.06em' }}>
        Recent
      </Text>
      {page.map((s) => {
        const counts = planCounts(s);
        // M6e §8: the same four words the shelf uses. `storyList` carries the read state now, so
        // "you started this and did not finish" is sayable about a story you wrote too.
        const badge = readBadge(s);
        return (
          <UnstyledButton
            key={s.id}
            component={Link}
            to={`/read/${s.id}`}
            style={{
              display: 'block',
              background: 'var(--nn-panel)',
              border: '1px solid var(--nn-hairline)',
              borderRadius: 9,
              padding: '10px 13px',
            }}
          >
            <div lang="ka" style={{ fontSize: 13.5, color: 'var(--nn-text-strong)' }}>
              {storyTitle(s)}
            </div>
            <div style={{ fontSize: 10.5, color: 'var(--nn-faint)', marginTop: 3 }}>
              {s.source === 'imported' ? 'yours' : 'generated'}
              {counts ? ` · ${counts.working} working · ${counts.fresh} new` : ''}
              {s.rating !== null ? ` · rated` : ''}
              {badge ? ` · ${badge}` : ''}
            </div>
          </UnstyledButton>
        );
      })}
    </Stack>
  );
}

// ---------------------------------------------------------------------------------------------
// /read/:storyId — the reader (`5a`) and the end (`5b`)
// ---------------------------------------------------------------------------------------------

export function ReadStory() {
  const { storyId } = useParams<{ storyId: string }>();
  const study = useStudy();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  // Read once, at mount: the pair is what marks the bite's read tile complete.
  const [biteScope] = useState(() => biteScopeFrom(searchParams));

  const storyQ = useQuery({
    queryKey: ['story', storyId],
    queryFn: () => api.storyGet(storyId!),
    enabled: !!storyId,
  });
  const story = storyQ.data ?? null;
  const lines = useMemo(() => (story ? storyLines(story) : []), [story]);
  const linesRef = useRef(lines);
  linesRef.current = lines;

  // ------------------------------------------------ marks (D2), exactly as Watch draws them
  const itemIds = useMemo(() => itemIdsOf(lines), [lines]);
  const itemsKey = itemIds.join(',');
  const memoryQ = useQuery({
    queryKey: ['memory', 'story', storyId, itemsKey],
    queryFn: () => api.memoryForItems(itemIds),
    enabled: itemIds.length > 0,
    staleTime: 60_000,
  });
  /**
   * M6e §3 — this story's saves, so the sheet can say "Saved ✓" instead of writing a second
   * identical row. Scoped to the story and capped at one page: a story is a few hundred words and
   * nobody saves more of them than that, so paging here would buy nothing.
   *
   * Deliberately NOT part of the gloss. A gloss row is cached and memoised across learners;
   * saved-ness is one learner's fact, and it lives beside the sheet rather than inside it.
   */
  const savedQ = useQuery({
    queryKey: ['saved', { storyId }],
    queryFn: () => api.savedList({ storyId: storyId!, limit: 200 }),
    enabled: !!storyId && study.entitled,
    staleTime: 60_000,
  });
  const saved = useMemo(() => savedKeys(savedQ.data?.items), [savedQ.data]);
  const savedRef = useRef(saved);
  savedRef.current = saved;

  const profile = useLiveProfile(study.me.profiles[0] ?? null);
  const display = profile?.display ?? null;
  const markDensity = display?.markDensity ?? 0.6;
  const marksHidden = display?.marksHidden ?? false;
  // A shared empty, not a fresh `{}` per render: the scroll handler below sets state on every
  // frame, and an object literal here would make `marks` a new map each time and re-render every
  // sentence of the story while the learner is scrolling it.
  const memory = memoryQ.data ?? NO_MEMORY;
  const marks = useMemo(() => {
    const out = new Map<string, Marks>();
    for (const line of lines) {
      out.set(line.id, marksFor(line, memory, { density: markDensity, hidden: marksHidden }));
    }
    return out;
  }, [lines, memory, markDensity, marksHidden]);
  const marksRef = useRef(marks);
  marksRef.current = marks;
  /** was the tapped token wearing a mark? (set at tap, read by the gloss call — spec § 2) */
  const markedAtRef = useRef(false);

  // ------------------------------------------------ EN visibility
  // An imported story has no translations and never will: `Transcript` would then print
  // "translation pending" under every sentence of it, which is a promise nobody made. A story
  // with nothing to reveal simply has EN off, and the control goes with it.
  const translatable = lines.some((l) => !!l.translation);
  const [enMode, setEnMode] = useState<EnMode>(
    study.me.profiles[0]?.display?.enVisibility ?? 'reveal',
  );
  const effectiveEn: EnMode = translatable ? enMode : 'hide';
  const [revealed, setRevealed] = useState<ReadonlySet<string>>(() => new Set());
  useEffect(() => setRevealed(new Set()), [enMode]);

  // ------------------------------------------------ activity lifecycle
  // One `story` activity spans the read AND the end checks: the checks are part of reading this
  // story, and `exerciseNext({ storyId })` wants the same activity id the exposure events carry.
  const activityRef = useRef<ActivityHandle | null>(null);
  const touchedRef = useRef<string[]>([]);
  const touch = useCallback((itemId: string) => {
    if (!touchedRef.current.includes(itemId)) touchedRef.current.push(itemId);
  }, []);
  // M6b: a culture note is its own activity type. It is a minute of English about Georgian, it
  // produces no `exposure`, and the goal ring's per-kind split should be able to tell it apart
  // from a story. Waiting for the story to load before starting means the type is right the first
  // time — restarting the activity when the kind arrived would end one and open another.
  const kind = storyQ.data?.kind ?? null;
  const activityType = kind === 'culture' ? 'culture' : 'story';
  /**
   * M6e §8 — did the learner get to the end of the story?
   *
   * `activity.abandoned` has existed since 0001 and NOTHING has ever sent it (M9 gap 2). This is
   * the first call site, and only this one: the other ten stay M9's, on purpose — a flag sent
   * from eleven places at once, all of them guessing at what "abandoned" means for their own
   * screen, is how the column would end up meaning nothing.
   *
   * Here it means one thing and the learner says it themselves: "Done reading" (or "Finish ·
   * quick check") moves to the end phase, and anything that ends the activity before that —
   * navigating away, closing the tab — is a story walked out of. The shelf's "left early" badge
   * is this flag, so the definition has to be one a reader would recognise.
   *
   * A ref, not state: it is read inside an effect's cleanup and inside a `pagehide` listener,
   * neither of which re-runs when a piece of state changes.
   */
  const finishedRef = useRef(false);
  useEffect(() => {
    if (!storyId || !kind) return;
    let cancelled = false;
    let handle: ActivityHandle | null = null;
    startActivity(study.session, kind === 'culture' ? 'culture' : 'story', { storyId }, biteScope)
      .then((a) => {
        if (cancelled) {
          void a.end();
          return;
        }
        handle = a;
        activityRef.current = a;
      })
      .catch((e) => console.warn('[read] activityStart failed; events carry activityId null', e));
    return () => {
      cancelled = true;
      activityRef.current = null;
      if (handle)
        void handle.end({ touchedItems: touchedRef.current, abandoned: !finishedRef.current });
    };
  }, [storyId, kind, study.session, biteScope]);
  useEffect(() => {
    // Deliberately NO `abandoned` here (M6e §8). `pagehide` fires when the app is backgrounded —
    // a phone call arrives mid-story — and the learner who comes back and finishes would then
    // wear "left early" on a piece they read to the end. A read that is not marked abandoned is
    // the safer error for a badge whose whole job is to nudge: the flag is sent from the unmount
    // above, which is the learner navigating away inside the app, and that IS walking out.
    const onHide = () => {
      void activityRef.current?.end({ touchedItems: touchedRef.current, keepalive: true });
    };
    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
  }, []);

  const emit = study.emit;
  const ctxBase = useCallback(
    () => ({ activityId: activityRef.current?.id ?? null, activityType }),
    [activityType],
  );

  // ------------------------------------------------ exposure + reveal
  // The gate lives here, on the screen, while the dwell clocks live in `StoryReader`. That split
  // is what makes going to the end checks and coming back harmless: the reader unmounts and its
  // ledger starts again, so every visible sentence reports a dwell a second time — and the gate,
  // which did not unmount, has already claimed them and emits nothing.
  const { expose } = useExposure(emit, ALREADY_ATTENDED);
  // M6b: which sentence the player is speaking, or -1. The reading-surface spec already says what
  // a playing line looks like (§4, fill only), so this is `Transcript`'s own `activeIdx`.
  const [spokenIdx, setSpokenIdx] = useState(-1);
  const onSpoken = useCallback((idx: number) => setSpokenIdx(idx), []);
  // A clip that actually played is a `replay` on that sentence — the same event Watch writes when
  // a line is looped, and the only evidence the listen axis gets from a piece.
  const onPlayed = useCallback(
    (sentenceIdx: number) => {
      if (!storyId) return;
      emit({
        ...ctxBase(),
        eventType: 'replay',
        modality: 'listen',
        context: { storyId, sentenceIdx },
      });
    },
    [emit, ctxBase, storyId],
  );

  const enRef = useRef({ enMode: effectiveEn, revealed });
  enRef.current = { enMode: effectiveEn, revealed };
  const onDwell = useCallback(
    (idx: number, dwellMs: number) => {
      if (!storyId) return;
      const line = linesRef.current[idx];
      if (!line?.items?.length) return;
      const { enMode: mode, revealed: seen } = enRef.current;
      const translationVisible = !!line.translation && (mode === 'show' || seen.has(line.id));
      const base = ctxBase();
      const rows: EmitInput[] = line.items.map((it) => ({
        ...base,
        eventType: 'exposure' as const,
        modality: 'read' as const,
        // The item id, and deliberately NO `surface`. The resolver's queue is "not resolved and
        // surface is not null", and it overwrites `item_id` from the surface match — a row
        // carrying both would have the resolver's guess written over the tag the story already
        // knows. With no surface the row is `ready` for the fold the moment it lands.
        itemId: it.itemId,
        latencyMs: dwellMs,
        support: { translationVisible },
        context: {
          storyId,
          // `line.idx`, not the row's position: the server keys `story_item` by the sentence's
          // own index, and `expandLineScoped` looks a `reveal` up by the same pair.
          sentenceIdx: line.idx,
          ...(it.charStart !== null && it.charEnd !== null
            ? { charStart: it.charStart, charEnd: it.charEnd }
            : {}),
        },
      }));
      expose(line.id, rows);
    },
    [ctxBase, expose, storyId],
  );

  const onReveal = useCallback(
    (line: Line) => {
      setRevealed((r) => new Set(r).add(line.id));
      // No item id and no surface: `reveal` is line-scoped, and the fold expands it over the
      // sentence's `story_item` rows (`expandLineScoped`). There is no dwell to report — unlike
      // Watch, where a line becomes active at a known instant, a sentence in a reader has been
      // on screen for however long the learner has been looking at it, which the dwell ledger
      // already reports on its own row.
      emit({
        ...ctxBase(),
        eventType: 'reveal',
        modality: 'read',
        context: { storyId, sentenceIdx: line.idx },
      });
    },
    [emit, ctxBase, storyId],
  );

  // ------------------------------------------------ the gloss sheet
  const [sheet, setSheet] = useState<GlossSheetState | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [savedState, setSavedState] = useState<SavedState>('idle');
  const [actionLine, setActionLine] = useState<string | null>(null);
  const glossTokenRef = useRef(0);
  /** per-session memo (selection + sentence → gloss): a re-tap is instant */
  const glossMemoRef = useRef(new Map<string, Gloss>());

  /**
   * One save, from either way in: the sheet's Save button (M4) or the double tap (M6e §4).
   *
   * The scoped `['saved', {storyId}]` query is invalidated on success, which is what makes the
   * sheet's "Saved ✓" and the gesture's `alreadySaved` guard agree with the server rather than
   * with a local flag that only this screen knows about.
   */
  /** Saved in THIS session, before the refetch lands — see `saveWord`. */
  const justSavedRef = useRef(new Set<string>());
  const saveWord = useCallback(
    (selection: string, sentence: string) => {
      if (!storyId || !sentence || !study.entitled) return;
      setSavedState('saving');
      api
        .savedCreate({
          targetLang: 'ka',
          baseLang: 'en',
          selection,
          sentence,
          storyId,
          synthesizeAudio: true,
        })
        .then(() => {
          // Recorded locally as well as invalidated: `savedCreate` writes a row every time it is
          // called, and between the POST resolving and the refetch landing the scoped set still
          // says this word is unsaved — which is long enough for an impatient second double-tap
          // to buy a duplicate.
          justSavedRef.current.add(savedKey(selection, sentence));
          setSavedState('saved');
          void queryClient.invalidateQueries({ queryKey: ['saved'] });
        })
        .catch(() => setSavedState('error'));
    },
    [storyId, study.entitled, queryClient],
  );

  const lookup = useCallback(
    (src: LookupSource, selection: string, charStart: number, charEnd: number) => {
      if (!storyId || !selection) return;
      // Every lookup here is a `'tap'`: this surface has no phrase selection (M3's swipe and
      // nudge belong to the transcript, where a line is a unit of speech). A tap inside a chunk
      // is still a tap — what makes it a chunk travels as `marked`, which is true by construction.
      const base = {
        selection,
        sentence: src.text,
        lineId: src.id,
        sel: { charStart, charEnd },
        mode: 'tap' as const,
      };
      setSavedState('idle');
      setActionLine(null);
      setSheetOpen(true);
      if (!study.entitled) {
        setSheet({ status: 'unentitled', ...base });
        return;
      }
      const tk = ++glossTokenRef.current;
      const memoKey = `${selection}\u0000${src.text}`;
      const memo = glossMemoRef.current.get(memoKey);
      setSheet(memo ? { status: 'done', ...base, gloss: memo } : { status: 'loading', ...base });
      // M10-i i-1: the instant layer, in flight beside the gloss. Nothing waits on it, and the
      // same `glossTokenRef` guards the race a second tap creates.
      // `settle` against `memo`: on a session-memo hit the gloss request short-circuits, so this
      // handler is the only one that runs and must do the reconcile itself.
      instantLookup(selection, src.text)
        .then((inst) => settle(inst, memo))
        .then((inst) => {
          if (glossTokenRef.current !== tk) return;
          setSheet((prev) => (prev ? { ...prev, instant: inst } : prev));
        });
      const { enMode: enNow, revealed: seen } = enRef.current;
      api
        .gloss({
          targetLang: 'ka',
          baseLang: 'en',
          selection,
          sentence: src.text,
          context: {
            sessionId: study.session.id,
            ...(activityRef.current ? { activityId: activityRef.current.id } : {}),
            storyId,
            // The whole reason a story lookup can reach the memory model. A story has no line
            // row, so `story_item` is keyed by (storyId, sentenceIdx, charStart) — without the
            // index the server's `lookup` event resolves to nothing and the tap teaches nothing.
            //
            // M6e §6: a tap in a CHECK QUESTION sends no index at all, because the word is not in
            // any story sentence. The resolver then searches the whole story's tags, which is
            // exactly right — story-v4 builds the question out of the story's own words, so the
            // word usually IS one of them, and when it is not the lemma cache is the honest
            // fallback. A fabricated index would attribute the lookup to a sentence that does not
            // contain the word.
            ...(src.sentenceIdx === null ? {} : { sentenceIdx: src.sentenceIdx }),
            translationVisible:
              src.translationVisible === true && (enNow === 'show' || seen.has(src.id)),
            selectionMode: 'tap' as const,
            marked: markedAtRef.current,
            markThreshold: markDensity,
            marksHidden,
          },
        })
        .then(async (gloss) => {
          glossMemoRef.current.set(memoKey, gloss);
          if (glossTokenRef.current !== tk || memo) return;
          // The dictionary facts survive the gloss unless the model disagrees about the LEMMA,
          // in which case they were about the wrong word — see lib/instant.ts `reconcile`.
          const settled = await settle(await instantLookup(selection, src.text), gloss);
          if (glossTokenRef.current !== tk) return;
          setSheet({ status: 'done', ...base, instant: settled, gloss });
        })
        .catch((err: unknown) => {
          if (glossTokenRef.current !== tk || memo) return;
          if (err instanceof ApiError && err.problem.status === 403) {
            setSheet({ status: 'unentitled', ...base });
            return;
          }
          setSheet({
            status: 'error',
            ...base,
            message:
              err instanceof ApiError && err.problem.detail
                ? err.problem.detail
                : 'That lookup didn’t reach us.',
          });
        });
    },
    [storyId, study.entitled, study.session.id, markDensity, marksHidden],
  );

  /**
   * M6e §4 — double tap to save. A second tap on the SAME word, inside `SAVE_TAP_WINDOW_MS`,
   * saves it and opens nothing further; anything else is the lookup it always was.
   *
   * ⟂ Spec departure, recorded not edited: `docs/design/reading-surface-spec.md` says saving is a
   * row-＋ affordance, not a text gesture. The spec is protected and is not being edited — the
   * citation is ROADMAP § M6e §4, and the ＋ still works and still means the same thing. This is a
   * second way in, on the word itself. The decision lives in `lib/save-gesture.ts` as a pure
   * function because this suite has no DOM.
   *
   * The reader has no phrase selection (M3's swipe and nudge belong to the transcript), so unlike
   * Watch there is nothing here for the gesture to collide with.
   */
  const armedRef = useRef<ArmedTap | null>(null);
  const onWordTap = useCallback(
    (token: Token, line: Line, tokIdx: number) => {
      const tap: ArmedTap = { key: savedKey(token.word, line.text), at: Date.now() };
      // `entitled` first: without it a second tap by an unentitled learner would take the save
      // branch, be refused inside `saveWord`, and do nothing at all — no save AND no lookup,
      // which is the one outcome worse than either.
      if (
        study.entitled &&
        decideWordTap(armedRef.current, tap, {
          alreadySaved: savedRef.current.has(tap.key) || justSavedRef.current.has(tap.key),
        }) === 'save'
      ) {
        armedRef.current = null; // a third tap is not a second save
        saveWord(token.word, line.text);
        return;
      }
      armedRef.current = tap;
      markedAtRef.current = marksRef.current.get(line.id)?.weak.has(tokIdx) === true;
      lookup(sourceOf(line), token.word, token.charStart, token.charEnd);
    },
    [lookup, saveWord, study.entitled],
  );
  /**
   * M6e §6 — a word tapped inside a Georgian check question.
   *
   * The question is not a sentence of the story, so the lookup carries no `sentenceIdx`: the
   * resolver then searches the whole story's tags, which is the right search — story-v4 builds
   * the question out of the story's own words, so the word usually IS one of them. There is no
   * mark to report (the question wears none) and no save gesture: saving lives on the reading
   * surface, and the checks screen is not it.
   */
  const onQuestionWord = useCallback(
    (token: Token, question: string) => {
      markedAtRef.current = false;
      lookup(
        { id: `check:${storyId ?? ''}`, text: question, sentenceIdx: null },
        token.word,
        token.charStart,
        token.charEnd,
      );
    },
    [lookup, storyId],
  );

  const onChunkTap = useCallback(
    (itemId: string, line: Line, charStart: number, charEnd: number) => {
      armedRef.current = null; // a chunk is not the word that was armed
      markedAtRef.current = true; // a chunk is always a mark
      touch(itemId);
      lookup(sourceOf(line), line.text.slice(charStart, charEnd), charStart, charEnd);
    },
    [lookup, touch],
  );

  // The content stays through the slide-down: the next tap replaces it, where clearing it now
  // would flash an empty sheet mid-animation.
  const closeSheet = useCallback(() => setSheetOpen(false), []);

  const sheetSelection = sheet?.selection ?? '';
  const sheetSentence = sheet?.sentence ?? '';
  const onSave = useCallback(
    () => saveWord(sheetSelection, sheetSentence),
    [saveWord, sheetSelection, sheetSentence],
  );

  // The button's state is the PLAYER's, not a local flag: `speak()` resolves when playback
  // starts, so a `.then(() => 'idle')` would drop the sheet back to "▶ Hear it" for the whole of
  // the clip and would not notice the learner stopping it from the mini bar. Watch reads it the
  // same way, off the same track key.
  const hearKey = `story-gloss:${sheet?.lineId ?? ''}:${sheetSelection}`;
  const hearStatus = useTtsKeyStatus(hearKey);
  const hearing: HearingState =
    hearStatus === 'loading' ? 'loading' : hearStatus === 'playing' ? 'playing' : 'idle';
  const onHear = useCallback(() => {
    if (!sheetSentence) return;
    setActionLine(null);
    speak(sheetSentence, { title: 'Story', subtitle: 'reading', key: hearKey }).catch(
      (err: unknown) => setActionLine(ttsCalmLine(err)),
    );
  }, [sheetSentence, hearKey]);

  // M6e §3: what the sheet says about saving. A selection already in this story's saved list
  // renders the action as spent — `savedCreate` would otherwise write a second identical row —
  // but only while nothing is in flight, so the save the learner just made still reads back as
  // "Saved · ▶" rather than jumping straight to the duller line.
  const sheetSaved: SavedState =
    savedState === 'idle' && saved.has(savedKey(sheetSelection, sheetSentence))
      ? 'already'
      : savedState;

  const sheetActions = {
    entitled: study.entitled,
    onSave,
    savedState: sheetSaved,
    onHear,
    hearing,
    actionLine,
  };

  // ------------------------------------------------ progress + the end
  const [progress, setProgress] = useState(0);
  // The scroll handler runs on every scroll event; the bar is drawn in whole percent. Rounding to
  // the drawn value means a flick down a long story re-renders this screen ~100 times rather than
  // once per frame, and React bails out of the renders where nothing moved.
  const onProgress = useCallback((f: number) => setProgress(Math.round(f * 100) / 100), []);
  const [reachedEnd, setReachedEnd] = useState(false);
  const [phase, setPhase] = useState<'read' | 'end'>('read');
  /** "Done reading" — the learner's own statement that they finished it. See `finishedRef`. */
  const finishReading = useCallback(() => {
    finishedRef.current = true;
    setPhase('end');
  }, []);
  const onReachedEnd = useCallback(() => setReachedEnd(true), []);

  const counts = story ? planCounts(story) : null;
  const title = story ? storyTitle(story) : '';

  if (storyQ.isPending) return <ReaderSkeleton onBack={() => navigate('/read')} />;
  if (!story) {
    return (
      <div style={{ padding: '18px 18px 0' }}>
        <Text fz={13} c="var(--nn-muted)">
          That story isn’t here. It may have been deleted.
        </Text>
        <UnstyledButton
          onClick={() => navigate('/read')}
          style={{ ...ghostButton, marginTop: 12, display: 'inline-flex' }}
        >
          Back to Read
        </UnstyledButton>
      </div>
    );
  }

  // M6b: a culture note is not read by the reading surface. `Transcript` tokenizes on whitespace
  // and offers every token as a Georgian tap target; over English prose that marks English words
  // and offers to gloss "feast". `CultureNote` renders the prose and makes only the Georgian runs
  // tappable — through the same `lookup` the reader uses, so a tap still writes its `lookup`.
  if (kind === 'culture') {
    return (
      <div style={screen}>
        <div style={bar}>
          <UnstyledButton onClick={() => navigate('/read')} aria-label="Back" style={barButton}>
            <IconBack />
          </UnstyledButton>
          <Text fz={13.5} fw={600} truncate style={{ minWidth: 0, flexShrink: 1 }}>
            {story.title ?? 'A note'}
          </Text>
          <span style={{ flex: 1 }} />
          <span
            style={{
              fontSize: 10.5,
              color: 'var(--nn-muted)',
              padding: '2px 8px',
              borderRadius: 999,
              border: '1px solid var(--nn-hairline)',
              whiteSpace: 'nowrap',
            }}
          >
            1 min
          </span>
        </div>

        <CultureNote
          note={story}
          onTerm={(term, paragraphIdx) => {
            const line = linesRef.current[paragraphIdx];
            if (!line) return;
            const at = line.text.indexOf(term);
            // The offsets are real, not fabricated: the term IS a slice of the paragraph, so the
            // gloss request carries the same (selection, sentence, offsets) shape a word tap does.
            markedAtRef.current = false;
            lookup(sourceOf(line), term, at < 0 ? 0 : at, at < 0 ? term.length : at + term.length);
          }}
        />

        <div style={footer}>
          <UnstyledButton onClick={() => navigate('/read')} style={{ ...emberButton, flex: 1 }}>
            Done
          </UnstyledButton>
        </div>

        <GlossDrawer
          opened={sheetOpen && sheet !== null}
          side="bottom"
          insetPx={0}
          state={sheet}
          onClose={closeSheet}
          resume={null}
          {...sheetActions}
        />
      </div>
    );
  }

  if (phase === 'end') {
    return (
      <>
        <StoryEnd
          story={story}
          title={title}
          activityId={activityRef.current?.id ?? null}
          onTouched={touch}
          onBack={() => setPhase('read')}
          onQuestionWord={onQuestionWord}
        />
        {/* M6e §6: the same sheet the reader uses, so a word tapped in a Georgian question is a
            lookup on the surface the learner already knows. */}
        <GlossDrawer
          opened={sheetOpen && sheet !== null}
          side="bottom"
          insetPx={0}
          state={sheet}
          onClose={closeSheet}
          resume={null}
          {...sheetActions}
        />
      </>
    );
  }

  return (
    <div style={screen}>
      {/* `5a`: 2 px of ember at the very top, and nothing else that could be mistaken for chrome. */}
      <div style={{ height: 2, background: 'var(--nn-hairline)', flexShrink: 0 }}>
        <div
          style={{
            height: 2,
            width: `${Math.round(progress * 100)}%`,
            background: 'var(--nn-accent)',
            transition: 'width .12s linear',
          }}
          role="progressbar"
          aria-label="How far through the story"
          aria-valuenow={Math.round(progress * 100)}
        />
      </div>

      <div style={bar}>
        <UnstyledButton onClick={() => navigate('/read')} aria-label="Back" style={barButton}>
          <IconBack />
        </UnstyledButton>
        <Text fz={13.5} fw={600} truncate lang="ka" style={{ minWidth: 0, flexShrink: 1 }}>
          {title}
        </Text>
        <span style={{ flex: 1 }} />
        {counts && (
          <span
            style={{
              fontSize: 10.5,
              color: 'var(--nn-muted)',
              padding: '2px 8px',
              borderRadius: 999,
              border: '1px solid var(--nn-hairline)',
              whiteSpace: 'nowrap',
            }}
          >
            {counts.working} working · {counts.fresh} new
          </span>
        )}
        <Menu position="bottom-end" width={220} shadow="md">
          <Menu.Target>
            <UnstyledButton aria-label="More" style={{ ...barButton, color: 'var(--nn-muted)' }}>
              <IconMore />
            </UnstyledButton>
          </Menu.Target>
          <Menu.Dropdown>
            {translatable && (
              <Menu.Item
                leftSection={<EnGlyph mode={enMode} />}
                onClick={() => setEnMode(EN_CYCLE[enMode])}
              >
                {EN_LABEL[enMode]}
              </Menu.Item>
            )}
            <Menu.Item onClick={finishReading}>Finish · quick check</Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </div>

      <StoryReader
        lines={lines}
        enMode={effectiveEn}
        revealed={revealed}
        marks={marks}
        activeIdx={spokenIdx}
        // A dialogue piece has voices but no timeline, so the chip is the only part of the
        // gutter that means anything and the seek affordance must not appear.
        gutter={story.kind === 'dialogue' ? 'speaker' : 'none'}
        {...(display?.transcriptSize ? { fontSizePx: display.transcriptSize } : {})}
        onWordTap={onWordTap}
        onChunkTap={onChunkTap}
        onReveal={onReveal}
        onProgress={onProgress}
        onDwell={onDwell}
        onReachedEnd={onReachedEnd}
      />

      {/* Anything with sentences can be heard as well as read (M6e §5). A shared piece is heard
          in its speakers' voices; a generated story has no voice map, and that is now the
          FALLBACK PATH rather than the reason for a gate — `pieceQueueLines` sends no voice for a
          sentence with no speaker, the server's `voiceFor` resolves the learner's own preferred
          voice, and "Read aloud" is what the control says. Nothing downstream changed: the player
          synthesizes per line through `api.tts`, which is entitlement-checked, cached by (text,
          voice) and budget-gated, and it already renders every refusal that route gives.

          The money, stated where the next reader of this line will need it: piece audio is warmed
          once and shared, but a generated story is one learner's text, so its sentences are
          near-guaranteed cache misses — a fresh ten-sentence story is roughly its own character
          count against `AZURE_TTS_MONTHLY_CHARS`. The cap and its 429 are honest, and the 429 is
          what the learner sees if the month runs out.

          Sentence-level highlight only. Word-level needs timings Azure's REST endpoint does not
          return; that is M10. */}
      {story.sentences.length > 0 && (
        <PiecePlayer story={story} onSentence={onSpoken} onPlayed={onPlayed} />
      )}

      {/* The end of the story is offered, never sprung: it appears once the last sentence has
          been on screen, and the learner decides when they have finished reading it. */}
      {reachedEnd && (
        <div style={footer}>
          <UnstyledButton onClick={finishReading} style={{ ...emberButton, flex: 1 }}>
            Done reading
          </UnstyledButton>
        </div>
      )}

      {/* The bottom sheet on every width. Watch docks the panel as a third column on desktop
          because the video has to keep playing beside it; a story does not move, so there is
          nothing for a docked panel to protect and one sheet is one behaviour to maintain. */}
      <GlossDrawer
        opened={sheetOpen && sheet !== null}
        side="bottom"
        insetPx={0}
        state={sheet}
        onClose={closeSheet}
        resume={null}
        {...sheetActions}
      />
    </div>
  );
}

const EN_CYCLE: Record<EnMode, EnMode> = { show: 'reveal', reveal: 'hide', hide: 'show' };
const EN_LABEL: Record<EnMode, string> = {
  show: 'EN · show',
  reveal: 'EN · tap',
  hide: 'EN · off',
};

// ---------------------------------------------------------------------------------------------
// the end of the story (`5b`)
// ---------------------------------------------------------------------------------------------

/**
 * Two comprehension checks, then the rating.
 *
 * The checks are the review machinery, not a second one: the exercises were built and persisted
 * at generation, `exerciseNext({ storyId })` hands them back, `exerciseAnswer` grades them and
 * writes the `retrieval_attempt`, and `lib/review-run.ts` holds the run. This screen is the `5b`
 * arrangement of them — a card rather than Review's keyboard-hugging prompt, because there is no
 * keyboard here and the rating has to sit under it.
 */
function StoryEnd({
  story,
  title,
  activityId,
  onTouched,
  onBack,
  onQuestionWord,
}: {
  story: Story;
  title: string;
  activityId: string | null;
  onTouched: (itemId: string) => void;
  onBack: () => void;
  /** M6e §6: a word tapped inside a Georgian check question → the gloss sheet. */
  onQuestionWord: (token: Token, question: string) => void;
}) {
  const navigate = useNavigate();
  const [run, setRun] = useState<RunState | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    // No activity id means `activityStart` never landed. There is nothing to scope the checks to
    // and nothing to attribute the attempt to, so the end is the rating alone.
    if (!activityId) {
      setFailed(true);
      return;
    }
    let cancelled = false;
    void api
      .exerciseNext({ activityId, storyId: story.id })
      .then((list) => {
        if (!cancelled) setRun(initRun(list, 'options'));
      })
      .catch(() => {
        // No checks is not a failure worth a screen: the rating is the rest of `5b` and still
        // works. The learner reads the story either way.
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [activityId, story.id]);

  const checksDone = failed || (run !== null && isDone(run));

  return (
    <div style={{ minHeight: '100%' }}>
      <div style={bar}>
        <UnstyledButton onClick={onBack} aria-label="Back to the story" style={barButton}>
          <IconBack />
        </UnstyledButton>
        <Text fz={13.5} fw={600} truncate lang="ka" style={{ minWidth: 0 }}>
          {title}
        </Text>
      </div>
      <div style={{ padding: '10px 20px 24px' }}>
        {!checksDone &&
          (run === null ? (
            <div style={panel}>
              <Skeleton height={11} width="35%" radius="sm" />
              <Skeleton height={16} width="80%" radius="sm" mt={10} />
              <Skeleton height={30} width="100%" radius="sm" mt={11} />
            </div>
          ) : (
            <Check
              run={run}
              setRun={setRun}
              activityId={activityId}
              onTouched={onTouched}
              onQuestionWord={onQuestionWord}
            />
          ))}

        {checksDone && <Rating storyId={story.id} />}

        {checksDone && (
          <div style={{ marginTop: 16, display: 'flex', gap: 9 }}>
            <UnstyledButton onClick={() => navigate('/read')} style={{ ...emberButton, flex: 1 }}>
              Read another
            </UnstyledButton>
            <UnstyledButton onClick={() => navigate('/')} style={{ ...ghostButton, flex: 1 }}>
              Done
            </UnstyledButton>
          </div>
        )}
      </div>
    </div>
  );
}

/** One check (`5b`). Chips, because a comprehension question has four Georgian words as answers. */
function Check({
  run,
  setRun,
  activityId,
  onTouched,
  onQuestionWord,
}: {
  run: RunState;
  setRun: (s: RunState) => void;
  activityId: string | null;
  onTouched: (itemId: string) => void;
  onQuestionWord: (token: Token, question: string) => void;
}) {
  const ex = currentExercise(run)!;
  const [busy, setBusy] = useState(false);
  const [calm, setCalm] = useState<string | null>(null);
  // Every check here is a comprehension check, so `expected` is always an option index.
  const shownAnswer = run.verdict ? expectedText(ex, run.verdict.expected) : null;
  // From the paint, not the fetch: the grader's z-score is about how long the learner took to
  // remember, and a slow network is not the learner being slow.
  const shownAt = useRef(Date.now());
  useEffect(() => {
    shownAt.current = Date.now();
    setCalm(null);
  }, [ex.id]);

  const submit = async (answer: unknown, mode: 'options' | 'shown') => {
    if (!activityId || busy) return;
    setBusy(true);
    try {
      const res = await api.exerciseAnswer({
        exerciseId: ex.id,
        activityId,
        answer,
        answerMode: mode,
        latencyMs: answerLatencyMs(shownAt.current, Date.now()),
        hintsUsed: 0,
        eventId: ulid(),
      });
      for (const id of ex.itemIds) onTouched(id);
      setRun(
        answered(
          run,
          {
            correct: res.correct,
            graded: res.graded,
            expected: res.expected,
            ...(res.explanation ? { explanation: res.explanation } : {}),
          },
          mode,
        ),
      );
    } catch {
      setCalm('That answer didn’t reach us. Try again in a moment.');
    } finally {
      setBusy(false);
    }
  };

  const options = ex.prompt.options ?? [];
  // The question as a `Line`, so the reading surface can render it. `id` is the exercise's, which
  // is what keeps the memoised row from being reused across two checks with the same text.
  const question = ex.prompt.text ?? '';
  const questionLine: Line = {
    id: `check:${ex.id}`,
    idx: 0,
    text: question,
    translation: null,
    startMs: 0,
    endMs: 0,
    // `startMs`/`endMs`/`speaker` are empty for the same reason `storyLines` empties them: there
    // is no timeline and no voice, and `gutter: 'none'` means none of them is ever drawn.
    speaker: '',
  };
  return (
    <div style={panel}>
      <div style={{ font: '10px/1 ui-monospace, Menlo, monospace', color: 'var(--nn-faint)' }}>
        QUICK CHECK · {run.idx + 1} OF {run.exercises.length}
      </div>
      {/* M6e §6. The question is GEORGIAN now (story-v4 / pieces-v7), so it is rendered through
          the same row component the story's sentences use: a hard word in the question is a
          lookup, not a wall. Every check written before those prompts is in English and is read
          back through the same field forever — nothing rewrites a stored row — so the decision is
          per question, and an English one is the plain text it always was.

          `marks` are deliberately not passed: a dotted word inside the question would be D2
          telling the learner which word they are weak on, which is a hint they did not earn. */}
      {isGeorgianQuestion(question) ? (
        <div className="nn-reading" style={{ marginTop: 9, margin: '9px -14px 0' }}>
          <TranscriptRow
            line={questionLine}
            idx={0}
            active={false}
            enMode="hide"
            revealed={false}
            selection={null}
            gutter="none"
            onWordTap={(token) => onQuestionWord(token, question)}
            onReveal={NOOP_REVEAL}
          />
        </div>
      ) : (
        <div style={{ marginTop: 9, fontSize: 14, color: 'var(--nn-text-strong)' }}>{question}</div>
      )}
      {!run.verdict && (
        <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginTop: 11 }}>
          {options.map((o, i) => (
            <UnstyledButton
              key={o}
              lang="ka"
              disabled={busy}
              onClick={() => void submit(i, 'options')}
              style={{
                fontSize: 14,
                padding: '7px 13px',
                borderRadius: 7,
                border: '1px solid color-mix(in srgb, var(--nn-text) 12%, transparent)',
                color: 'var(--nn-text)',
              }}
            >
              {o}
            </UnstyledButton>
          ))}
        </div>
      )}
      {run.verdict && (
        <>
          <div
            style={{
              marginTop: 11,
              fontSize: 12,
              color: run.verdict.correct ? 'var(--nn-accent-text)' : 'var(--nn-muted)',
            }}
          >
            {run.verdict.correct ? 'Right' : 'Not quite'}
            {/* The chips are gone by now, so without this "Show answer" showed no answer. */}
            {!run.verdict.correct && shownAnswer !== null && (
              <>
                {' — '}
                <span lang="ka" style={{ color: 'var(--nn-text-strong)' }}>
                  {shownAnswer}
                </span>
              </>
            )}
            {run.verdict.explanation ? ` — ${run.verdict.explanation}` : ''}
          </div>
          <UnstyledButton
            onClick={() => setRun(advance(run))}
            style={{ ...emberButton, marginTop: 12 }}
          >
            {run.idx + 1 < run.exercises.length ? 'Next' : 'Finish'}
          </UnstyledButton>
        </>
      )}
      {!run.verdict && (
        <div style={{ display: 'flex', gap: 14, marginTop: 11 }}>
          <UnstyledButton
            onClick={() => setRun(skipPrompt(run))}
            style={{ fontSize: 12, color: 'var(--nn-muted)' }}
          >
            Skip
          </UnstyledButton>
          <UnstyledButton
            onClick={() => void submit('', 'shown')}
            style={{ fontSize: 12, color: 'var(--nn-muted)' }}
          >
            Show answer
          </UnstyledButton>
        </div>
      )}
      {calm && (
        <Text fz={11} c="var(--nn-muted)" mt={8} role="status">
          {calm}
        </Text>
      )}
    </div>
  );
}

/** 👍/👎 and the three chips (`5b`). Ratings are curation signal, never a re-score of this read. */
function Rating({ storyId }: { storyId: string }) {
  const [rating, setRating] = useState<number | null>(null);
  const [reason, setReason] = useState<'too_easy' | 'too_hard' | 'boring' | null>(null);
  const [calm, setCalm] = useState<string | null>(null);

  const post = (next: { rating: number; reason?: 'too_easy' | 'too_hard' | 'boring' }) => {
    setCalm(null);
    api.storyRate(storyId, next).catch(() => setCalm('That rating didn’t reach us.'));
  };

  return (
    <div style={{ ...panel, marginTop: 14 }}>
      <div style={{ fontSize: 13, color: 'var(--nn-text)' }}>How was this story?</div>
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, flexWrap: 'wrap' }}
      >
        {[
          { value: 4, label: 'Good' },
          { value: 2, label: 'Not good' },
        ].map((t) => (
          <UnstyledButton
            key={t.value}
            aria-label={t.label}
            aria-pressed={rating === t.value}
            onClick={() => {
              setRating(t.value);
              post({ rating: t.value, ...(reason ? { reason } : {}) });
            }}
            style={{
              width: 38,
              height: 34,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 7,
              border: '1px solid var(--nn-hairline)',
              background:
                rating === t.value
                  ? 'color-mix(in srgb, var(--nn-accent) 16%, transparent)'
                  : undefined,
              color: rating === t.value ? 'var(--nn-accent-text)' : 'var(--nn-text)',
            }}
          >
            <IconThumb down={t.value === 2} />
          </UnstyledButton>
        ))}
        <span style={{ width: 1, height: 22, background: 'var(--nn-hairline)', margin: '0 4px' }} />
        {(
          [
            ['too_easy', 'too easy'],
            ['too_hard', 'too hard'],
            ['boring', 'boring'],
          ] as const
        ).map(([value, label]) => (
          <UnstyledButton
            key={value}
            aria-pressed={reason === value}
            onClick={() => {
              setReason(value);
              // `StoryRate.rating` is required, so a chip tapped on its own has to carry one.
              // All three chips name something wrong with the fit — including "too easy", which
              // is a complaint about the level — so on their own they read as 👎. A thumb the
              // learner actually pressed always wins over that reading.
              const r = rating ?? 2;
              setRating(r);
              post({ rating: r, reason: value });
            }}
            style={{
              fontSize: 11,
              padding: '3px 9px',
              borderRadius: 999,
              border: '1px solid var(--nn-hairline)',
              background:
                reason === value
                  ? 'color-mix(in srgb, var(--nn-accent) 16%, transparent)'
                  : undefined,
              color: reason === value ? 'var(--nn-accent-text)' : 'var(--nn-text)',
            }}
          >
            {label}
          </UnstyledButton>
        ))}
      </div>
      {calm && (
        <Text fz={11} c="var(--nn-muted)" mt={8} role="status">
          {calm}
        </Text>
      )}
    </div>
  );
}

/** Traced from `5b`; the thumb-down is the same path, turned over. */
function IconThumb({ down }: { down: boolean }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={down ? { transform: 'rotate(180deg)' } : undefined}
      aria-hidden
    >
      <path d="M7 10.5v9H4.5a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1H7zM7 11l4-6.5a1.8 1.8 0 0 1 3.3 1.2L13.6 10h4.9a2 2 0 0 1 2 2.4l-1.2 5.5a2 2 0 0 1-2 1.6H7" />
    </svg>
  );
}

function ReaderSkeleton({ onBack }: { onBack: () => void }) {
  return (
    <div style={screen}>
      <div style={{ height: 2, background: 'var(--nn-hairline)', flexShrink: 0 }} />
      <div style={bar}>
        <UnstyledButton onClick={onBack} aria-label="Back" style={barButton}>
          <IconBack />
        </UnstyledButton>
        <Skeleton height={13} width={140} radius="sm" />
      </div>
      <div style={{ padding: '14px 22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {['92%', '84%', '96%', '70%'].map((w) => (
          <Skeleton key={w} height={17} width={w} radius="sm" />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// shared surfaces
// ---------------------------------------------------------------------------------------------

/** The reader owns the whole viewport under the chrome; the sentences scroll, nothing else does. */
const screen: React.CSSProperties = {
  height: 'calc(100dvh - var(--app-shell-footer-offset, 0rem))',
  display: 'flex',
  flexDirection: 'column',
  background: 'var(--nn-bg)',
};

const bar: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  height: 44,
  padding: '0 4px',
  gap: 2,
  flexShrink: 0,
  minWidth: 0,
};

const barButton: React.CSSProperties = {
  width: 40,
  height: 40,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--nn-text)',
  flexShrink: 0,
};

const panel: React.CSSProperties = {
  background: 'var(--nn-panel)',
  border: '1px solid var(--nn-hairline)',
  borderRadius: 12,
  padding: 16,
};

const emberButton: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '8px 14px',
  borderRadius: 7,
  background: 'var(--nn-accent)',
  color: 'var(--nn-on-accent)',
  fontSize: 12.5,
  fontWeight: 600,
};

const ghostButton: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '8px 14px',
  borderRadius: 7,
  border: '1px solid color-mix(in srgb, var(--nn-text) 12%, transparent)',
  color: 'var(--nn-text)',
  fontSize: 12.5,
};

const footer: React.CSSProperties = {
  display: 'flex',
  gap: 9,
  padding: '10px 18px calc(10px + env(safe-area-inset-bottom))',
  borderTop: '1px solid var(--nn-hairline)',
  background: 'var(--nn-bg)',
  flexShrink: 0,
};

/**
 * The reader's own stylesheet. It has to be concatenated into `App.tsx`'s single `<style>` beside
 * `READING_SURFACE_CSS` — that wiring is NOT done yet (App.tsx is not this step's file), and
 * without it the reader renders at 1.66 in the sans and `5c`'s shimmer does not animate.
 *
 * Three rules, each here for a reason that inline styles cannot express:
 *
 *  - **line-height.** `theme.ts` sets `.nn-reading { line-height: 1.66 }` as a CLASS rule, which
 *    beats anything the reader could inherit or set on a wrapper's `style`. `5a` is 17/1.75, so
 *    the only way to get it — without editing a protected artifact — is a rule of equal weight
 *    and greater specificity.
 *  - **the serif.** Noto Serif Georgian is the story reader's face and nothing else's (README
 *    §5). It is hung off `[lang="ka"]` so it lands on the Georgian sentences and NOT on the EN
 *    line under them, which stays in the app's sans exactly as `5a` draws it.
 *  - **the shimmer.** `5c`'s generating lines need a keyframe, and keyframes cannot be inlined.
 *    `prefers-reduced-motion` stops it: the three bars still say "something is being written",
 *    which is the whole of their information.
 */
export const READER_CSS = `
.nn-reader .nn-reading { line-height: 1.75; }
.nn-reader .nn-reading [lang="ka"] { font-family: 'Noto Serif Georgian', serif; }
@keyframes nnShimmer { 0%, 100% { opacity: .45 } 50% { opacity: 1 } }
.nn-shimmer {
  display: block; height: 11px; border-radius: 4px;
  background: var(--nn-hairline);
  animation: nnShimmer 1.6s ease-in-out infinite;
}
.nn-reader-input {
  width: 100%; box-sizing: border-box;
  background: color-mix(in srgb, var(--nn-text) 4%, transparent);
  border: 1px solid var(--nn-hairline);
  border-radius: 8px; padding: 9px 12px;
  color: var(--nn-text-strong);
  font: 12.5px/1.5 'Noto Sans Georgian', sans-serif; outline: none;
}
.nn-reader-input::placeholder { color: var(--nn-faint); }
.nn-reader-input:focus { border-color: color-mix(in srgb, var(--nn-accent) 65%, transparent); }
@media (prefers-reduced-motion: reduce) { .nn-shimmer { animation: none; } }
`;
